/**
 * BuildBadge — surfaces the live iter + build date (and runtime env
 * marker) on both the login screen and the /admin Village Control
 * Centre. Iter 31.89 (27 May 2026) per Six's directive.
 *
 * Two render modes:
 *   - default (label + iter): unobtrusive footer pill for the login screen
 *   - variant="card": fuller block for the Admin status board
 *
 * Reads `/api/build-info` once on mount. Public endpoint — works
 * pre-auth so the login splash can render it.
 */
import React, { useEffect, useState } from "react";
import { Hash, Calendar, Hourglass } from "lucide-react";
import { api } from "@/lib/api";

const ENV_TONE = {
    production: { color: "text-stamp-red", label: "PROD" },
    preview:    { color: "text-stamp-green", label: "PREVIEW" },
    pod:        { color: "text-ink-soft", label: "POD" },
};


function formatUptime(seconds) {
    if (!Number.isFinite(seconds)) return "—";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
    return `${Math.round(seconds / 86400)}d`;
}


export default function BuildBadge({ variant = "default", className = "" }) {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const { data } = await api.get("/build-info");
                if (live) setInfo(data);
            } catch (_e) {
                // Endpoint not available (e.g. older preview before iter 31.89
                // is deployed) — render a quiet placeholder.
                if (live) setInfo({ iter: "—", date: "—", env: "pod" });
            }
        })();
        return () => { live = false; };
    }, []);

    if (!info) return null;
    const env = ENV_TONE[info.env] || ENV_TONE.pod;

    if (variant === "card") {
        return (
            <div
                className={`paper-card border-2 border-ink/30 p-4 mb-6 ${className}`}
                data-testid="build-badge-card"
            >
                <div className="flex items-baseline justify-between gap-3 mb-2">
                    <h2 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Build manifest
                    </h2>
                    <span
                        className={`font-heading uppercase tracking-[0.22em] text-[10px] ${env.color}`}
                        data-testid="build-badge-env"
                    >
                        · {env.label} ·
                    </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                        <div className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft inline-flex items-center gap-1">
                            <Hash className="w-3 h-3" /> Iter
                        </div>
                        <div className="font-heading text-stamp-red text-xl mt-0.5" data-testid="build-badge-iter">
                            {info.iter}
                        </div>
                    </div>
                    <div>
                        <div className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Build date
                        </div>
                        <div className="font-heading text-ink text-sm mt-1.5" data-testid="build-badge-date">
                            {info.date}
                        </div>
                    </div>
                    <div>
                        <div className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft inline-flex items-center gap-1">
                            <Hourglass className="w-3 h-3" /> Uptime
                        </div>
                        <div className="font-heading text-ink text-sm mt-1.5" data-testid="build-badge-uptime">
                            {formatUptime(info.uptime_seconds)}
                        </div>
                    </div>
                    <div>
                        <div className="font-heading uppercase tracking-[0.18em] text-[10px] text-ink-soft">
                            Host
                        </div>
                        <div className="font-body text-[10px] text-ink-faint mt-1.5 truncate" title={info.host}>
                            {info.host}
                        </div>
                    </div>
                </div>
                {info.tag ? (
                    <div className="label-typed text-[10px] mt-3 opacity-80">{info.tag}</div>
                ) : null}
            </div>
        );
    }

    // default: compact footer pill for login screen
    return (
        <div
            className={`inline-flex items-center gap-2 font-heading uppercase tracking-[0.22em] text-[10px] text-ink-soft ${className}`}
            data-testid="build-badge"
            title={`${info.tag || ""} · host ${info.host} · uptime ${formatUptime(info.uptime_seconds)}`}
        >
            <span>Iter <span className="text-stamp-red">{info.iter}</span></span>
            <span className="opacity-60">·</span>
            <span>{info.date}</span>
            <span className="opacity-60">·</span>
            <span className={env.color} data-testid="build-badge-env">{env.label}</span>
        </div>
    );
}
